import React, { Children, forwardRef, ReactElement, useMemo } from 'react';
import styled from 'styled-components';
import { ButtonGroupContext } from '../../components/Button/ButtonGroupContext';
import { SIZES } from '../../fundamentals/sizes/sizes';
import { useFullWidthOnSmallCss } from '../../fundamentals/breakpoints/breakpoints';
import { DEFAULT_BUTTON_VARIANT, Button, IButtonProps, ButtonSize } from '../../components/Button/Button';
import { Dropdown } from '../Dropdown/Dropdown';
import { DropdownToggle } from '../Dropdown/DropdownToggle';
import { DropdownMenu } from '../Dropdown/DropdownMenu';
import { DropdownItem } from '../DropdownItem/DropdownItem';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { useWindowDimensions } from '../../hooks/useResponsive/useResponsive';
import { resolveProps } from '../../common/utils';
import { BaseDiv } from '../../common/BaseElements';
import { BUTTON_VARIANTS, ButtonVariant } from '../../components/Button/buttonVariants';
import { IMenuBaseProps } from '../Menu/MenuBase';

export interface IButtonGroupBaseProps
  extends Pick<IButtonProps, 'size' | 'disabled' | 'fullWidth' | 'variant' | 'mode'>,
    Partial<Pick<ReturnType<typeof useWindowDimensions>, 'isTablet' | 'isMobile'>>,
    Pick<IMenuBaseProps, 'debounceInMs' | 'asMenu'> {
  variant?: ButtonVariant;
}

export interface IButtonGroupProps extends IButtonGroupBaseProps {
  value?: string | string[];
  multiple?: boolean;
  children: ReactElement<typeof Button> | ReactElement<typeof Button>[];
  onChange?: (value: string | string[] | undefined) => void;
}

const ButtonContainer = styled(BaseDiv)<IButtonGroupProps>`
  display: inline-flex;
  position: relative;
  ${useFullWidthOnSmallCss};
`;

const isSelected = (value: string | string[] | undefined, multiple: boolean, target: string) =>
  multiple ? Array.isArray(value) && value.includes(target) : value === target;

export const handleValueChange = (
  currentValue: string | string[] | undefined,
  newValue: string,
  multiple: boolean,
  onChange?: (value: string | string[] | undefined) => void
) => {
  if (!onChange) return;

  if (multiple) {
    const current = Array.isArray(currentValue) ? currentValue : [];
    const updated = current.includes(newValue) ? current.filter((v) => v !== newValue) : [...current, newValue];
    onChange(updated);
  } else {
    onChange(newValue);
  }
};

export const renderDropdownContent = (
  childrenArray: ReactElement<IButtonProps>[],
  value: string | string[] | undefined,
  multiple: boolean,
  onChange: ((value: string | string[] | undefined) => void) | undefined,
  size: ButtonSize
) => {
  const selectedItems = childrenArray.filter((child) => isSelected(value, multiple, String(child.props.value ?? '')));

  const renderSelectedLabel = () => {
    if (multiple) {
      return selectedItems.length > 0 ? selectedItems.map((c) => c.props.children).join(', ') : 'Select';
    }
    const itemToShow = selectedItems[0] ?? childrenArray[0];
    return itemToShow?.props.children ?? 'Select';
  };

  return (
    <ButtonGroupContext.Provider value={{ isGroup: false }}>
      <Dropdown drop='down'>
        <DropdownToggle>
          <Button selected variant={BUTTON_VARIANTS.PRIMARY} size={size} endIcon='expand-more'>
            {renderSelectedLabel()}
          </Button>
        </DropdownToggle>
        <DropdownMenu>
          {childrenArray.map((child) => {
            const buttonValue = String(child.props.value ?? '');
            const selected = isSelected(value, multiple, buttonValue);
            return (
              <DropdownItem key={buttonValue} selected={selected} onClick={() => handleValueChange(value, buttonValue, multiple, onChange)}>
                {child.props.children}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </Dropdown>
    </ButtonGroupContext.Provider>
  );
};

export const renderButtonContent = (
  childrenArray: ReactElement<IButtonProps>[],
  value: string | string[] | undefined,
  multiple: boolean,
  onChange: ((value: string | string[] | undefined) => void) | undefined,
  contextValue: any,
  props: IButtonGroupProps
) => {
  return (
    <ButtonGroupContext.Provider value={contextValue}>
      {childrenArray.map((child) => {
        const buttonValue = String(child.props.value ?? '');
        return React.cloneElement(child, {
          selected: isSelected(value, multiple, buttonValue),
          disabled: props.disabled || child.props.disabled,
          onClick: () => handleValueChange(value, buttonValue, multiple, onChange),
          ...contextValue
        });
      })}
    </ButtonGroupContext.Provider>
  );
};

export const buttonGroupContent = (
  childrenArray: ReactElement<IButtonProps>[],
  value: string | string[] | undefined,
  multiple: boolean,
  onChange: ((value: string | string[] | undefined) => void) | undefined,
  isMobile: boolean | undefined,
  isTablet: boolean | undefined,
  contextValue: any,
  props: IButtonGroupProps
) => {
  const shouldUseDropdown = isMobile || isTablet;

  return shouldUseDropdown
    ? renderDropdownContent(childrenArray, value, multiple, onChange, contextValue.size)
    : renderButtonContent(childrenArray, value, multiple, onChange, contextValue, props);
};

export const ButtonGroup = forwardRef<HTMLDivElement, React.PropsWithChildren<IButtonGroupProps>>(
  ({ children, size = SIZES.MEDIUM, value, onChange, multiple = false, variant = DEFAULT_BUTTON_VARIANT, mode, ...props }, ref) => {
    const { generateTestId } = useTestId(props, 'ButtonGroup');
    const defaultProps = useWindowDimensions();
    const { isTablet, isMobile } = resolveProps({ ...defaultProps }, { ...props });

    const contextValue = useMemo(
      () => ({
        isTablet,
        isMobile,
        variant,
        size,
        isGroup: true,
        ...props
      }),
      [isTablet, isMobile, variant, size, props]
    );

    const childrenArray = Children.toArray(children) as ReactElement<IButtonProps>[];

    return (
      <ButtonContainer ref={ref} {...generateTestId()} {...props}>
        {buttonGroupContent(
          childrenArray as ReactElement<IButtonProps>[],
          value,
          multiple,
          onChange,
          isMobile,
          isTablet,
          contextValue,
          props as IButtonGroupProps
        )}
      </ButtonContainer>
    );
  }
);
